"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { Check, Copy, X } from "lucide-react";
import { onCheckoutModal } from "@/lib/checkoutModal";
import { lockScroll } from "@/lib/scrollLock";
import { clockParts, copyText, offerLive, useNow, useOffer } from "@/lib/promo";

const DELAY_MS = 5000;
const ARRIVED_KEY = "rb-offer-arrived";

/**
 * The discount offer's popup (admin → Discount offer → Messages). Opens 5 seconds after someone arrives on the
 * website (counted from the first page of the visit, so moving between pages doesn't restart it), once per visitor
 * for each offer. Phones: a sheet that slides up from the bottom. Larger screens: a two-part card in the middle.
 * Closes with ×, "Maybe later", Escape or a click outside; never opens over the checkout window.
 */
export function OfferPopup() {
  const offer = useOffer();
  const now = useNow(!!offer);
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const cta = useRef<HTMLAnchorElement>(null);
  const key = offer ? `rb-offer-popup-${offer.id}` : "";

  // When to open: 5 s after arriving, unless this offer's popup was already closed here.
  useEffect(() => {
    if (!offer || !offer.show_popup) return;
    let seen = false;
    let arrived = Date.now();
    try {
      seen = window.localStorage.getItem(key) === "closed";
      const stored = Number(window.sessionStorage.getItem(ARRIVED_KEY));
      if (stored > 0) arrived = stored;
      else window.sessionStorage.setItem(ARRIVED_KEY, String(arrived));
    } catch {
      // storage blocked: show it once on this page
    }
    if (seen) return;
    let checkoutOpen = false;
    const off = onCheckoutModal((req) => (checkoutOpen = !!req));
    const t = window.setTimeout(
      () => {
        if (!checkoutOpen && offerLive(offer, Date.now())) setOpen(true);
      },
      Math.max(600, DELAY_MS - (Date.now() - arrived)),
    );
    return () => {
      window.clearTimeout(t);
      off();
    };
  }, [offer, key]);

  const close = () => {
    try {
      window.localStorage.setItem(key, "closed");
    } catch {
      // closed for this page only
    }
    setClosing(true);
    window.setTimeout(() => {
      setOpen(false);
      setClosing(false);
    }, 260);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    const unlock = lockScroll();
    const t = window.setTimeout(() => cta.current?.focus({ preventScroll: true }), 350);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.clearTimeout(t);
      unlock();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!offer || !open || now == null || !offerLive(offer, now)) return null;
  const left = clockParts(Date.parse(offer.ends_at) - now);

  return (
    <div
      className={`rb-offer-pop fixed inset-0 z-[130] flex items-end justify-center sm:items-center sm:p-6 ${closing ? "is-closing" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="rb-offer-title"
    >
      <style>{`
        .rb-offer-pop { --pink: #FF8FCF }
        .rb-offer-back { animation: rb-offer-fade .35s ease both }
        .rb-offer-card { animation: rb-offer-up .5s cubic-bezier(.2,.9,.25,1.12) both }
        @media (min-width: 640px) { .rb-offer-card { animation-name: rb-offer-pop } }
        .rb-offer-pop.is-closing .rb-offer-back { animation: rb-offer-fade .26s ease reverse both }
        .rb-offer-pop.is-closing .rb-offer-card { animation: rb-offer-out .26s ease both }
        @keyframes rb-offer-fade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes rb-offer-up { from { transform: translateY(100%) } to { transform: none } }
        @keyframes rb-offer-pop { from { opacity: 0; transform: translateY(18px) scale(.94) } to { opacity: 1; transform: none } }
        @keyframes rb-offer-out { to { opacity: 0; transform: translateY(24px) scale(.97) } }
        @keyframes rb-offer-spin { to { transform: rotate(360deg) } }
        @keyframes rb-offer-float { 0%, 100% { transform: translateY(0) } 50% { transform: translateY(-8px) } }
        @keyframes rb-offer-shine { 0% { background-position: 0% 50% } 100% { background-position: 200% 50% } }
        .rb-offer-ring { animation: rb-offer-spin 12s linear infinite }
        @keyframes rb-offer-twinkle { 0%, 100% { opacity: .15; transform: scale(.6) rotate(0deg) } 50% { opacity: .95; transform: scale(1) rotate(45deg) } }
        .rb-offer-star { animation: rb-offer-twinkle 3.2s ease-in-out infinite }
        @keyframes rb-offer-breathe { 0%, 100% { opacity: .35 } 50% { opacity: .6 } }
        .rb-offer-glow { animation: rb-offer-breathe 5s ease-in-out infinite }
        @keyframes rb-offer-ping2 { 0% { transform: scale(1); opacity: .8 } 80%, 100% { transform: scale(2.6); opacity: 0 } }
        .rb-offer-ping { animation: rb-offer-ping2 1.6s cubic-bezier(0,0,.2,1) infinite }
        .rb-offer-float { animation: rb-offer-float 4s ease-in-out infinite }
        .rb-offer-num { background: linear-gradient(100deg, #fff 20%, #FFD1EA 40%, #fff 60%); background-size: 200% 100%; -webkit-background-clip: text; background-clip: text; color: transparent; animation: rb-offer-shine 4s linear infinite }
        .rb-offer-btn { background: linear-gradient(110deg, #6a52ff, #8f67ff 35%, #f0569e 70%, #6a52ff); background-size: 220% 100%; animation: rb-offer-shine 5s linear infinite }
        @media (prefers-reduced-motion: reduce) {
          .rb-offer-back, .rb-offer-card, .rb-offer-ring, .rb-offer-float, .rb-offer-num, .rb-offer-btn, .rb-offer-star, .rb-offer-glow, .rb-offer-ping { animation: none !important }
        }
      `}</style>
      <button type="button" className="rb-offer-back absolute inset-0 cursor-default bg-[#0b0820]/65 backdrop-blur-[6px]" aria-label="Close" onClick={close} />

      <div className="rb-offer-card relative max-h-[94dvh] w-full max-w-[820px] overflow-y-auto overscroll-contain rounded-t-[28px] sm:max-h-[calc(100dvh-48px)] bg-white shadow-[0_40px_120px_-30px_rgba(40,20,140,0.75)] sm:grid sm:grid-cols-[0.92fr_1.08fr] sm:rounded-[30px]">
        <button
          type="button"
          onClick={close}
          className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white/85 ring-1 ring-white/20 transition hover:bg-white/20 hover:text-white sm:right-4 sm:top-4 sm:bg-transparent sm:text-ink-softer sm:ring-0 sm:hover:bg-violet-pale sm:hover:text-ink"
          aria-label="Close"
        >
          <X size={18} />
        </button>
        {/* The number: big, glowing, centred in its panel, with what each package gets and the time left */}
        <div className="relative overflow-hidden bg-[radial-gradient(130%_100%_at_30%_0%,#6a4cf0_0%,#2f1f8f_42%,#140e38_78%,#0e0a28_100%)] px-6 pb-6 pt-5 text-white sm:flex sm:flex-col sm:px-8 sm:py-8">
          {/* lights */}
          <div className="rb-offer-glow pointer-events-none absolute -right-24 -top-28 h-72 w-72 rounded-full bg-[#f0569e] opacity-45 blur-[80px]" />
          <div className="pointer-events-none absolute -bottom-28 -left-20 h-64 w-64 rounded-full bg-[#38bdf8] opacity-25 blur-[80px]" />
          <div className="pointer-events-none absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,.35) 1px, transparent 1px)", backgroundSize: "22px 22px", maskImage: "radial-gradient(ellipse at 50% 45%, #000 15%, transparent 70%)", WebkitMaskImage: "radial-gradient(ellipse at 50% 45%, #000 15%, transparent 70%)" }} />
          {/* twinkling stars */}
          {[["12%", "22%", 0], ["84%", "30%", 1.1], ["18%", "70%", 2.2], ["78%", "74%", 0.6], ["50%", "12%", 1.7]].map(([l, t, d]) => (
            <svg key={`${l}${t}`} className="rb-offer-star pointer-events-none absolute hidden h-3.5 w-3.5 text-white sm:block" style={{ left: l as string, top: t as string, animationDelay: `${d}s` }} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M12 0c.7 6.4 4.9 10.6 12 12-7.1 1.4-11.3 5.6-12 12-.7-6.4-4.9-10.6-12-12C7.1 10.6 11.3 6.4 12 0z" />
            </svg>
          ))}
          {/* the panel's edge, like a tear-off coupon */}
          <div className="pointer-events-none absolute inset-y-6 right-0 hidden border-r-2 border-dashed border-white/15 sm:block" aria-hidden />
          {/* phones: a handle like an app sheet */}
          <div className="mx-auto mb-3 h-1.5 w-11 rounded-full bg-white/30 sm:hidden" aria-hidden />

          <div className="relative sm:text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/12 py-1 pl-2 pr-3 text-[0.66rem] font-extrabold uppercase tracking-[0.1em] ring-1 ring-white/25 backdrop-blur-sm">
              <span className="h-2 w-2 rounded-full bg-[var(--pink)] shadow-[0_0_10px_var(--pink)]" />
              {offer.label}
            </span>
          </div>

          {/* the middle: everything centred in the height that's left */}
          <div className="relative sm:flex sm:flex-1 sm:flex-col sm:items-center sm:justify-center sm:py-6">
            <div className="rb-offer-float relative mt-3 flex items-end gap-3 sm:mt-0 sm:flex-col sm:items-center sm:gap-0">
              {/* glowing orb and spinning ring behind the number */}
              <div className="pointer-events-none absolute left-1/2 top-1/2 hidden h-[250px] w-[250px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(160,120,255,.45)_0%,rgba(240,86,158,.18)_45%,transparent_70%)] sm:block" aria-hidden />
              <div
                className="rb-offer-ring pointer-events-none absolute left-1/2 top-1/2 hidden h-[270px] w-[270px] -translate-x-1/2 -translate-y-1/2 rounded-full sm:block"
                style={{ background: "conic-gradient(from 0deg, transparent 0 10%, rgba(255,143,207,.95) 22%, transparent 34%, transparent 52%, rgba(143,123,255,.95) 64%, transparent 76%)", mask: "radial-gradient(circle, transparent 66%, #000 67%, #000 68.5%, transparent 69.5%)", WebkitMask: "radial-gradient(circle, transparent 66%, #000 67%, #000 68.5%, transparent 69.5%)" }}
                aria-hidden
              />
              <div className="pointer-events-none absolute left-1/2 top-1/2 hidden h-[214px] w-[214px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-white/15 sm:block" aria-hidden />
              <div className="relative text-[0.8rem] font-bold uppercase tracking-[0.18em] text-white/70">{offer.min_percent !== offer.max_percent ? "Up to" : "Save"}</div>
              <div className="rb-offer-num relative text-[4.2rem] font-black leading-[0.85] tracking-[-0.05em] drop-shadow-[0_10px_30px_rgba(240,86,158,0.35)] sm:mt-1 sm:text-[6.6rem]">
                {offer.max_percent}%
              </div>
              <div className="relative pb-1 sm:mt-2 sm:pb-0">
                <span className="inline-block rounded-full bg-gradient-to-r from-[#FF8FCF] to-[#f0569e] px-3 py-0.5 text-[1.1rem] font-black uppercase tracking-[0.12em] text-white shadow-[0_8px_24px_-6px_rgba(240,86,158,0.8)] sm:px-4 sm:text-[1.25rem]">
                  off
                </span>
              </div>
            </div>

            {/* what each package gets */}
            {offer.codes.length > 0 && (
              <div className="relative mt-4 hidden flex-wrap justify-center gap-1.5 sm:mt-9 sm:flex">
                {offer.codes.map((c) => (
                  <span key={c.code} className="inline-flex items-center gap-1.5 rounded-full bg-white/[0.08] py-1 pl-1 pr-3 text-[0.74rem] font-bold ring-1 ring-white/15 backdrop-blur-sm">
                    <span className="rounded-full bg-white px-2 py-0.5 text-[0.68rem] font-black text-[#2a1b7a]">-{c.percent}%</span>
                    {c.plans}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="relative mt-4 sm:mt-0">
            <div className="mb-2 flex items-center gap-2 text-[0.68rem] font-bold uppercase tracking-[0.14em] text-white/60 sm:justify-center">
              <span className="relative flex h-1.5 w-1.5">
                <span className="rb-offer-ping absolute inline-flex h-full w-full rounded-full bg-[var(--pink)]" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[var(--pink)]" />
              </span>
              Offer ends in
            </div>
            <div className="flex gap-2 sm:justify-center" aria-label="Time left">
              {left.d > 0 && <Block v={String(left.d)} l="days" />}
              <Block v={left.h} l="hours" />
              <Block v={left.m} l="min" />
              <Block v={left.s} l="sec" />
            </div>
          </div>
        </div>

        {/* The words, the codes and the button */}
        <div className="relative px-6 pt-6 sm:px-9 sm:py-9">
          <h2 id="rb-offer-title" className="sm:pr-8 text-[1.45rem] font-extrabold leading-[1.15] tracking-[-0.02em] text-ink sm:text-[1.75rem]">
            {offer.popup_title}
          </h2>
          <p className="mt-3 text-[0.9rem] leading-relaxed text-ink-soft sm:text-[0.95rem]">{offer.popup_text}</p>

          {offer.codes.length > 0 && (
            <ul className="mt-5 space-y-2">
              {offer.codes.map((c) => (
                <li key={c.code} className="flex items-center gap-3 rounded-2xl border border-dashed border-violet/40 bg-violet-pale/60 py-2 pl-3 pr-2">
                  <span className="flex h-9 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#8f67ff] to-[#f0569e] text-[0.82rem] font-black text-white">
                    -{c.percent}%
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[0.74rem] font-bold text-ink-softer">{c.plans}</span>
                    <span className="block font-mono text-[0.95rem] font-extrabold tracking-[0.08em] text-ink">{c.code}</span>
                  </span>
                  <button
                    type="button"
                    onClick={async () => {
                      if (await copyText(c.code)) {
                        setCopied(c.code);
                        window.setTimeout(() => setCopied(null), 1600);
                      }
                    }}
                    className="flex flex-shrink-0 items-center gap-1.5 rounded-xl bg-white px-3 py-2 text-[0.76rem] font-extrabold text-violet-deep shadow-sm ring-1 ring-violet/20 transition hover:ring-violet/50"
                  >
                    {copied === c.code ? <Check size={14} /> : <Copy size={13} />}
                    {copied === c.code ? "Copied" : "Copy"}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-2 text-[0.74rem] text-ink-softer">
            No need to type it: the discount is added at checkout by itself.
            {offer.first_purchase_only ? " For your first plan purchase." : ""}
          </p>

          {/* Phones: the button stays in view at the bottom of the sheet */}
          <div className="sticky bottom-0 -mx-6 mt-4 bg-white/95 px-6 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 shadow-[0_-12px_24px_-16px_rgba(40,20,140,0.35)] backdrop-blur sm:static sm:mx-0 sm:mt-0 sm:bg-transparent sm:p-0 sm:shadow-none sm:backdrop-blur-none">
          <Link
            ref={cta}
            href="/pricing"
            onClick={close}
            className="rb-offer-btn flex sm:mt-5 w-full items-center justify-center gap-2 rounded-full px-6 py-[14px] text-[0.98rem] font-extrabold text-white shadow-[0_18px_36px_-14px_rgba(106,82,255,0.9)] transition hover:-translate-y-0.5 focus:outline-none focus-visible:ring-4 focus-visible:ring-violet/30"
          >
            {offer.popup_button} <span aria-hidden>→</span>
          </Link>
          <div className="mt-3 flex items-center justify-center gap-4 text-[0.8rem]">
            <button type="button" onClick={close} className="font-bold text-ink-softer hover:text-ink">
              Maybe later
            </button>
            {offer.link && (
              <>
                <span className="text-ink-softer/50" aria-hidden>
                  ·
                </span>
                <a href={offer.link} target="_blank" rel="noopener noreferrer" className="font-bold text-violet-deep hover:underline">
                  Support us on Product Hunt
                </a>
              </>
            )}
          </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Block({ v, l }: { v: string; l: string }) {
  return (
    <span className="flex min-w-[52px] flex-col items-center rounded-xl bg-gradient-to-b from-white/[0.16] to-white/[0.05] px-2 py-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.18),0_10px_24px_-12px_rgba(0,0,0,0.6)] ring-1 ring-white/15 backdrop-blur-sm sm:min-w-[60px] sm:py-2">
      <b className="tabular-nums text-[1.2rem] font-black leading-none sm:text-[1.45rem]">{v}</b>
      <span className="mt-1 text-[0.58rem] font-bold uppercase tracking-[0.1em] text-white/55">{l}</span>
    </span>
  );
}
